import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileCheck, ShieldAlert } from "lucide-react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Product } from "@/api";
import { productKeys } from "@/api/hooks/use-products";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
import { Button } from "@osmix/ui/button";
export const complianceSummaryKey = ["compliance-summary"] as const;

/**
 * Полоса над списком товаров (17.10.2026): сколько карточек опубликовано и
 * ждёт модерации без документа о соответствии. «Опубликованы без документа»
 * - фильтр таблицы; снятие с продажи - отдельной кнопкой администратора с
 * подтверждением. Пока ни одна категория документ не требует, полосы нет.
 */
export function ComplianceStrip({
  active,
  status,
  onShow,
  onReset
}: {
  active: boolean;
  status: string;
  onShow: (status: "published" | "moderation") => void;
  onReset: () => void;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canModerate = hasAdminPermission(user, "action.products.moderate");
  const summary = useQuery({
    queryKey: complianceSummaryKey,
    queryFn: () => api.compliance.summary()
  });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const data = summary.data;
  if (!data || data.requiredCategories === 0 && !active && !data.pendingDocuments) return null;
  const unpublish = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.compliance.unpublishMissing();
      setNotice(t("compliance.products.unpublishDone", {
        count: res.count,
        shops: res.shops
      }));
      setConfirming(false);
      await Promise.all([queryClient.invalidateQueries({
        queryKey: complianceSummaryKey
      }), queryClient.invalidateQueries({
        queryKey: productKeys.all
      })]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30" data-testid="compliance-strip">
			<ShieldAlert className="size-4 text-amber-700" aria-hidden />
			{active && <span className="font-medium">{t("compliance.products.filterOn")}</span>}
			<Button size="sm" variant={active && status === "published" ? "default" : "outline"} onClick={() => onShow("published")} data-testid="compliance-published-missing">
				{t("compliance.products.publishedMissing", {
        count: data.publishedMissing
      })}
			</Button>
			<Button size="sm" variant={active && status === "moderation" ? "default" : "outline"} onClick={() => onShow("moderation")}>
				{t("compliance.products.moderationMissing", {
        count: data.moderationMissing
      })}
			</Button>
			{data.held > 0 && <span className="text-muted-foreground">{t("compliance.products.held", {
        count: data.held
      })}</span>}
			{(data.pendingDocuments ?? 0) > 0 && <Button size="sm" variant="outline" asChild>
					<Link to="/compliance" data-testid="compliance-queue-link">
						<FileCheck className="size-4" /> {t("compliance.products.pendingDocuments", {
          count: data.pendingDocuments
        })}
					</Link>
				</Button>}
			{active && <Button size="sm" variant="ghost" onClick={onReset}>
					{t("compliance.products.showAll")}
				</Button>}
			<div className="flex-1" />
			{canModerate && <Button size="sm" variant="outline" className="text-destructive" disabled={data.publishedMissing === 0} onClick={() => setConfirming(true)} data-testid="compliance-unpublish">
					{t("compliance.products.unpublish")}
				</Button>}
			{notice && <span className="basis-full text-emerald-700" role="status">
					{notice}
				</span>}
			<ConfirmDialog open={confirming} busy={busy} title={t("compliance.products.unpublishTitle")} description={t("compliance.products.unpublishBody", {
      count: data.publishedMissing
    })} confirmLabel={t("compliance.products.unpublishConfirm")} confirmVariant="destructive" onConfirm={() => void unpublish()} onClose={() => setConfirming(false)}>
				{error && <p className="text-sm text-destructive">{error}</p>}
			</ConfirmDialog>
		</div>;
}

/** Подпись под статусом строки: чего не хватает карточке. */
export function ComplianceRowNote(t0) {
  const $ = _c(10);
  const {
    product
  } = t0;
  const {
    t
  } = useTranslation();
  const c = product.compliance;
  if (!c) {
    return null;
  }
  if (product.status === "blocked" && c.hold) {
    let t1;
    if ($[0] !== c.hold || $[1] !== t) {
      t1 = c.hold === "expired" ? t("compliance.products.holdExpired") : c.hold === "rejected" ? t("compliance.products.holdRejected") : t("compliance.products.holdMissing");
      $[0] = c.hold;
      $[1] = t;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t1) {
      t2 = <span className="block text-xs text-destructive">{t1}</span>;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (!c.blocksPublication || product.status === "archived") {
    return null;
  }
  const key = c.state === "expired" ? "rowExpired" : c.state === "rejected" ? "rowRejected" : c.state === "pending" ? "rowPending" : "rowMissing";
  const t1 = `compliance.products.${key}`;
  let t2;
  if ($[5] !== t || $[6] !== t1) {
    t2 = t(t1);
    $[5] = t;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <span className="block text-xs text-amber-700" data-testid="product-compliance-note">{t2}</span>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}